import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../translations/translations';
import '../styles/ticket.css';

const TicketVerify = () => {
  const { language } = useLanguage();
  const { ticketId } = useParams();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scanning, setScanning] = useState(false);

  const fetchTicketDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/get-ticket/${ticketId}`);
      
      if (!response.ok) {
          throw new Error(t('ticket.display.ticketNotFound', language));
      }
      
      const ticketData = await response.json();
      setTicket(ticketData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [ticketId, language]);

  useEffect(() => {
    fetchTicketDetails();
  }, [fetchTicketDetails]);

  const handleScan = async () => {
    if (!ticket || scanning) return;
    
    try {
      setScanning(true);
      const response = await fetch(`/api/scan-ticket`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ticketId: ticketId }),
      });

      if (response.ok) {
        // Refresh ticket data to get updated scan count
        await fetchTicketDetails();
      }
    } catch (err) {
      console.error('Error scanning ticket:', err);
    } finally {
      setScanning(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString(language === 'pl' ? 'pl-PL' : 'en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Use the API's calculation for expiration and active status
  const isExpired = ticket && ticket.isExpired;
  const isActive = ticket && ticket.isActive;

  if (loading) {
    return (
      <div className="ticket-verify">
        <Header />
        <div className="ticket-verify-loading">
          <h1>{t('ticket.verify.verifyingTicket', language)}</h1>
          <p>{t('ticket.verify.pleaseWait', language)}</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="ticket-verify">
        <Header />
        <div className="ticket-verify-error">
          <h1>{t('ticket.verify.invalidTicket', language)}</h1>
          <p>{t('ticket.verify.ticketNotExist', language)}</p>
          <p>{t('ticket.verify.checkQR', language)}</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="ticket-verify">
      <Header />
      
      <div className="ticket-verify-container">
        <div className="ticket-verify-header">
          <h1>{t('ticket.verify.ticketVerification', language)}</h1>
          <p>Pawilon Krzyżanowskiego</p>
        </div>

        <div className="ticket-verify-content">
          {/* Ticket Status */}
          <div className={`ticket-status ${isActive ? 'active' : 'expired'}`}>
            {isActive ? (
              <div className="status-active">
                <h2>{t('ticket.verify.validTicket', language)}</h2>
                <p>{t('ticket.verify.validForEntry', language)}</p>
              </div>
            ) : (
              <div className="status-expired">
                <h2>{t('ticket.verify.expiredTicket', language)}</h2>
                <p>{t('ticket.verify.noLongerValid', language)}</p>
              </div>
            )}
          </div>

          {/* Ticket Details */}
          <div className="ticket-details">
            <div className="ticket-info">
              <div className="ticket-number">
                <span className="label">{t('ticket.verify.ticketNumber', language)}</span>
                <span className="value">{ticket.ticketNumber}</span>
              </div>
              
              <div className="ticket-id">
                <span className="label">{t('ticket.verify.ticketId', language)}</span>
                <span className="value">{ticket.ticketId}</span>
              </div>
            </div>

            <div className="event-details">
              <h3>{ticket.eventName}</h3>
              <p className="event-date">{formatDate(ticket.eventDate)}</p>
              {ticket.eventThumbnail && (
                <img src={ticket.eventThumbnail} alt={ticket.eventName} className="event-thumbnail" />
              )}
            </div>

            <div className="attendee-details">
              <h4>{t('ticket.verify.attendeeInformation', language)}</h4>
              <p><strong>{t('ticket.verify.name', language)}:</strong> {ticket.attendeeName}</p>
              {ticket.buyerEmail && (
                <p><strong>{t('ticket.verify.email', language)}:</strong> {ticket.buyerEmail}</p>
              )}
            </div>

            <div className="ticket-stats">
              <div className="scan-count">
                <span className="label">{t('ticket.verify.scanned', language)}</span>
                <span className="value">{ticket.scanCount} {t('ticket.verify.times', language)}</span>
              </div>
              <div className="purchase-date">
                <span className="label">{t('ticket.verify.purchased', language)}</span>
                <span className="value">{formatDate(ticket.purchaseDate)}</span>
              </div>
            </div>
          </div>

          {/* Scan Button */}
          {isActive && (
            <div className="scan-section">
              <button 
                className="scan-button"
                onClick={handleScan}
                disabled={scanning}
              >
                {scanning ? t('ticket.verify.scanning', language) : t('ticket.verify.markAsScanned', language)}
              </button>
              <p className="scan-note">
                {t('ticket.verify.clickToMark', language)}
              </p>
            </div>
          )}

          {/* Expiration Info */}
          {isExpired && (
            <div className="expiration-info">
              <p><strong>{t('ticket.verify.expiredOn', language)}:</strong> {formatDate(ticket.expiresAt)}</p>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TicketVerify;
